// Какво пише на един ред от хронологията: текстът на чипа, тонът му, тихите
// редове под него и сумата вдясно.
//
// Цялата „кой event как се казва" логика стои ТУК, а не в компонента, за да
// може редът да остане маркъп без разклонения — и за да се вижда на едно
// място, че разликата между отстъпка, надбавка и промяна на цена е само в
// знака на процента и в `discount_type`.

import type { TFunction } from "i18next";

import { formatMinutes, parseDecimal } from "../../../../components/dataTable";
import { fmtEuro } from "../../../../utils/currency";
import type { OpEvent } from "../../../../api/billOperations";
import type { EventTone } from "./billOperationsFormat";

export interface EventLook {
  /** Текстът в чипа — „Плащане · В брой", „Отваряне · Маса 24". */
  label: string;
  tone: EventTone;
  /** Тихите редове под чипа: причина, сектор, продължителност, клиент. */
  notes: string[];
  /** Сумата вдясно на реда; `null` = операцията не носи сума. */
  amount: number | null;
  /** Плащането показва КОЛКО е платено — без знак пред числото. */
  amountUnsigned: boolean;
}

interface EventContext {
  /** Името на текущата сметка — участва в „Отваряне · Маса 24". */
  billName: string;
  /** Сервитьорът на сметката; чуждият се изписва при разделяне. */
  billWaiterId: number | null;
  t: TFunction;
  minuteLabels: { hour: string; minute: string };
}

/**
 * Промяна на цена — същият event като отстъпката върху продукт, но записан с
 * discount_type „custom_price" (BE пази новата цена като процент върху реда).
 */
export const isPriceChange = (event: OpEvent): boolean =>
  !!event.is_item_level && event.discount_type === "custom_price";

/** Знаков процент: `< 0` е надбавка и се изписва с „+". */
const formatPercent = (event: OpEvent): string | null => {
  if (event.discount_percent == null) return null;
  const percent = Number(event.discount_percent);
  if (!Number.isFinite(percent)) return null;
  return percent < 0 ? `+${Math.abs(percent)}%` : `${percent}%`;
};

const isSurcharge = (event: OpEvent): boolean =>
  event.discount_percent != null && Number(event.discount_percent) < 0;

/** „Отваряне" + „Маса 24" → „Отваряне · Маса 24". */
const withQualifier = (label: string, qualifier?: string | null): string =>
  qualifier ? `${label} · ${qualifier}` : label;

/** Кой ред е тръгнал накъде при разделяне/прехвърляне. */
const moveNote = (event: OpEvent, context: EventContext): string | null => {
  if (!event.other_bill) return null;
  const incoming = event.type === "split_in";
  const from = incoming ? event.other_bill.name : context.billName;
  const to = incoming ? context.billName : event.other_bill.name;
  const otherWaiter = event.other_bill.waiter;
  const showWaiter =
    otherWaiter && otherWaiter.id !== context.billWaiterId
      ? otherWaiter.names || `#${otherWaiter.id}`
      : null;
  return showWaiter ? `${from} → ${to} · ${showWaiter}` : `${from} → ${to}`;
};

/** Разбивката на смесеното плащане; празно при плащане с един метод. */
const mixedPaymentNote = (
  event: OpEvent,
  t: TFunction,
): string | null => {
  const parts: string[] = [];
  if (event.mixed_cash && Number(event.mixed_cash) > 0) {
    parts.push(`${t("reportsPage.billOpsPaidCash")} ${fmtEuro(event.mixed_cash)}`);
  }
  if (event.mixed_card && Number(event.mixed_card) > 0) {
    parts.push(`${t("reportsPage.billOpsPaidCard")} ${fmtEuro(event.mixed_card)}`);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
};

const discountLabel = (event: OpEvent, t: TFunction): string => {
  const removed = event.type === "discount_remove";
  if (isPriceChange(event)) {
    return t(
      removed
        ? "reportsPage.billOpsEventPriceRestored"
        : "reportsPage.billOpsEventPriceChange",
    );
  }
  if (event.is_item_level) {
    return t(
      removed
        ? "reportsPage.billOpsEventDiscountItemRemoved"
        : "reportsPage.billOpsEventDiscountItem",
    );
  }
  if (isSurcharge(event)) {
    return t(
      removed
        ? "reportsPage.billOpsEventSurchargeBillRemoved"
        : "reportsPage.billOpsEventSurchargeBill",
    );
  }
  return t(
    removed
      ? "reportsPage.billOpsEventDiscountBillRemoved"
      : "reportsPage.billOpsEventDiscountBill",
  );
};

export const describeEvent = (
  event: OpEvent,
  context: EventContext,
): EventLook => {
  const { t } = context;
  const change = parseDecimal(event.change);
  // Плащането и затварянето идват с `change = 0` (BE ги пише информативно),
  // затова сумата им се вади от running balance-а, а не от промяната.
  const base: EventLook = {
    label: "",
    tone: "neutral",
    notes: [],
    amount: change !== 0 ? change : null,
    amountUnsigned: false,
  };

  switch (event.type) {
    case "open":
      return {
        ...base,
        label: withQualifier(
          t("reportsPage.billOpsEventOpen"),
          context.billName,
        ),
        tone: "neutral",
        notes: event.sector?.names
          ? [t("reportsPage.billOpsSector", { name: event.sector.names })]
          : [],
      };

    case "add_items":
      return {
        ...base,
        label: t("reportsPage.billOpsEventAddItems"),
        tone: "brand",
      };

    case "split_in": {
      const note = moveNote(event, context);
      return {
        ...base,
        label: t("reportsPage.billOpsEventSplitIn"),
        tone: "info",
        notes: note ? [note] : [],
      };
    }

    case "split_out": {
      const note = moveNote(event, context);
      const notes: string[] = note ? [note] : [];
      // Пълното прехвърляне е ДРУГА операция за оператора („преместихме
      // гостите"), макар BE да я праща като изходящо разделяне.
      if (event.is_full_transfer) {
        if (event.other_bill) {
          notes.push(
            t("reportsPage.billOpsFullTransfer", { id: event.other_bill.id }),
          );
        }
        return {
          ...base,
          label: t("reportsPage.billOpsEventTransfer"),
          tone: "info",
          notes,
        };
      }
      return {
        ...base,
        label: t("reportsPage.billOpsEventSplitOut"),
        tone: "info",
        notes,
      };
    }

    case "cancel": {
      const notes: string[] = [];
      if (event.reason) {
        const reason = t("reportsPage.billOpsReason", { reason: event.reason });
        notes.push(
          event.is_defective
            ? `${reason} · ${t("reportsPage.billOpsDefective")}`
            : reason,
        );
      } else if (event.is_defective) {
        notes.push(t("reportsPage.billOpsDefective"));
      }
      return {
        ...base,
        label: t("reportsPage.billOpsEventCancel"),
        tone: "danger",
        notes,
      };
    }

    case "discount_apply":
    case "discount_remove": {
      const percent = formatPercent(event);
      const notes: string[] = [];
      if (event.client_name) {
        notes.push(t("reportsPage.billOpsClient", { name: event.client_name }));
      }
      return {
        ...base,
        // Процентът стои в чипа, а не на отделен ред: той Е операцията.
        // При промяна на цена го няма — там редът показва „стара → нова".
        label: withQualifier(
          discountLabel(event, t),
          isPriceChange(event) ? null : percent,
        ),
        tone: event.type === "discount_remove" ? "danger" : "violet",
        notes,
      };
    }

    case "preliminary_bill":
      return {
        ...base,
        label: t("reportsPage.billOpsEventPreliminaryBill"),
        tone: "neutral",
      };

    case "payment": {
      const note = mixedPaymentNote(event, t);
      return {
        ...base,
        label: withQualifier(
          t("reportsPage.billOpsEventPayment"),
          event.payment_method_name,
        ),
        tone: "ok",
        notes: note ? [note] : [],
        amount: parseDecimal(event.balance_after),
        amountUnsigned: true,
      };
    }

    case "close":
      return {
        ...base,
        label: t("reportsPage.billOpsEventClose"),
        tone: "neutral",
        notes:
          event.duration_minutes != null
            ? [
                t("reportsPage.billOpsDuration", {
                  value: formatMinutes(
                    event.duration_minutes,
                    context.minuteLabels,
                  ),
                }),
              ]
            : [],
      };

    case "restore": {
      // Сметката е отворена наново със същото id: плащането и затварянето
      // над този ред са отменени, бонът им — сторниран с кредитно известие.
      const notes: string[] = [];
      if (event.reason) {
        notes.push(t("reportsPage.billOpsReason", { reason: event.reason }));
      }
      if (event.previous_receipt_number) {
        notes.push(
          t("reportsPage.billOpsRestoreReceipt", {
            number: event.previous_receipt_number,
          }),
        );
      }
      // Сметката е ИЗКЛЮЧЕНА от отчетите (те са преизчислени), не остава в тях.
      if (event.previous_report_number != null) {
        notes.push(
          event.previous_establishment_report_number != null
            ? t("reportsPage.billOpsRestoreReportBoth", {
                number: event.previous_report_number,
                establishment: event.previous_establishment_report_number,
              })
            : t("reportsPage.billOpsRestoreReport", {
                number: event.previous_report_number,
              }),
        );
      }
      return {
        ...base,
        label: t("reportsPage.billOpsEventRestore"),
        tone: "info",
        notes,
      };
    }

    default:
      // Нов тип от BE-то не бива да чупи прозореца — показва се със
      // суровото си име, докато FE-то го научи.
      return { ...base, label: event.type, tone: "neutral" };
  }
};
